import { useState } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { fetcher } from '../utils/fetchers/fetcher';
import { resolveApiPath } from '../utils/resolveApiPath';
import { apiRequest } from '../utils/apiRequest';
import { detailedApiMethodFetcher } from '../utils/fetchers/detailedApiMethodFetcher';
import { Body, HandlerRequestContext } from '../types/types';
import { DetailedApiMethod, JSONObject, LayoutObject } from '../../types/types';

export const useGenerateMatrix = () => {
  const {
    state: { token },
  } = useUserState();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const handleApiError = useApiErrorHandling();
  const { getResponseActionHandler } = useResponseActionsContext();

  const generate = async (
    api: string,
    matrixValues: JSONObject,
    forceHeader?: string
  ): Promise<LayoutObject[] | undefined> => {
    try {
      setIsGenerating(true);
      setError(undefined);
      return await fetcher<LayoutObject[]>(
        resolveApiPath(api),
        'POST',
        token,
        forceHeader,
        undefined,
        matrixValues as unknown as Body
      );
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, true, (retryForceHeader) =>
        generate(api, matrixValues, retryForceHeader)
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // apiRequest resolves the {{ }} templates against resolveData, fires the
  // request and runs the method's responseActions on the response. The body
  // goes in AFTER the resolution — user values must not be mistaken for
  // templates.
  const detailedGenerate = async (
    apiMethod: DetailedApiMethod,
    resolveData: HandlerRequestContext,
    matrixValues: JSONObject,
    forceHeader?: string
  ): Promise<LayoutObject[] | undefined> => {
    try {
      setIsGenerating(true);
      setError(undefined);
      return await apiRequest<LayoutObject[]>(
        apiMethod,
        resolveData,
        (resolvedMethod) =>
          detailedApiMethodFetcher<LayoutObject[]>(
            {
              ...resolvedMethod,
              httpMethod: resolvedMethod.httpMethod ?? 'POST',
              content: matrixValues,
            },
            forceHeader
          ),
        getResponseActionHandler
      );
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, true, (retryForceHeader) =>
        detailedGenerate(apiMethod, resolveData, matrixValues, retryForceHeader)
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return { isGenerating, error, generate, detailedGenerate };
};
